import { Box, Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import { useLogout } from "api/services/useAuth";
import { getConfig } from "config/config";
import { useFetchCustom } from "hooks/useFetchCustom";
import { Typography } from "new-components/typography/Typography";
import { AuthFlow, User } from "types/users";
import { replaceWindowLocation } from "utils/utilityFunctions";

const config = getConfig();
const { JANZU_URL, REACT_APP_LOGOUT_URL } = config;

export interface IDPMigrationModalProps {
  isOpen: boolean;
  user: User;
}

export function IDPMigrationModal(props: IDPMigrationModalProps) {
  const { isOpen, user } = props;

  const [error, setError] = useState<string | null>(null);

  const { get } = useFetchCustom(JANZU_URL.slice(0, -1));

  const requestInProgress = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const { refetch: logout } = useLogout();

  useEffect(() => {
    const migrationUrl = `${JANZU_URL}reef-api/idp-migration`;

    const handleMigration = async () => {
      if (isOpen && user.auth_flow === AuthFlow.COGNITO && !requestInProgress.current) {
        requestInProgress.current = true;
        try {
          const migrateResponse = await fetch(migrationUrl, { method: 'GET', cache: 'no-cache' });
          if (!migrateResponse.ok) {
            throw new Error('Migration failed, please contact support');
          }
        } catch (e: any) {
          setError(e.message);
        } finally {
          requestInProgress.current = false;
        }
      }
      if (isOpen) {
        timeoutRef.current = setTimeout(() => {
          logout();
          replaceWindowLocation(REACT_APP_LOGOUT_URL);
        }, 20000);
      }
    };

    handleMigration();

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen, get, user.auth_flow, logout]);

  const handleMigrationClick = async () => {
    setError(null); // Clear any previous errors
    logout();
    replaceWindowLocation(REACT_APP_LOGOUT_URL);
  };

  return (
    <Dialog data-testid="idp-migration-modal" open={isOpen} fullWidth maxWidth="sm">
      <DialogTitle className="flex justify-between justify-center items-center bg-zinc-200">
        <Typography variant="main" align="center">
          Groundlight AI Account Update
        </Typography>
      </DialogTitle>
      <DialogContent className="p-6">
        <Typography className="mb-2">
          We recently updated our login platform. To complete the login migration process, 
          check your email at <b>{user.email}</b> now and create a new password using the link we have just sent you.
        </Typography>
        <Typography className="mb-2">
          This migration applies to login credentials only. None of your data or Groundlight services will be affected.
        </Typography>

        {error && (
          <Typography align="center" className="text-red-500 mb-4">
            {error}
          </Typography>
        )}

        <Box className="flex justify-center mt-4">
          <Button
            className="bg-[#0450BA] text-white text-xl min-w-[300px]"
            variant="outlined"
            onClick={handleMigrationClick}
          >
            LOGOUT
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
